import React from 'react';
import {
  IconButton,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
  Typography,
} from '@one-thd/sui-atomic-components';
import {
  ArrowForward,
  Cancel,
  CreditCard,
  Delivery,
  Returns,
} from '@one-thd/sui-icons';
import { IViewChargeHistoryResponse } from '../../../models/responses/oms';
import { useViewChargedDetailsContext } from '../../../hooks/useContexts';

type Transaction = IViewChargeHistoryResponse['transactions'][number];

const getTransactionIcon = (transactionType: string) => {
  const type = transactionType.toLowerCase();
  if (type.includes('cancel')) return Cancel;
  if (type.includes('ship') || type.includes('deliver')) return Delivery;
  if (type.includes('return')) return Returns;
  return CreditCard;
};

const formatTransactionDate = (dateStr: string): string => {
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTenderSubtitle = (transaction: Transaction): string => {
  const { transactionDate, tenders } = transaction;
  const formattedDate = formatTransactionDate(transactionDate);
  if (!tenders || tenders.length === 0) return formattedDate;
  const tender = tenders[0];
  const tenderLabel =
    tender.type && tender.value
      ? `${tender.type} - ${tender.value}`
      : tender.type || '';
  return tenderLabel ? `${formattedDate} | ${tenderLabel}` : formattedDate;
};

export const ViewChargedDetailsSteps: React.FC<{
  transactions?: IViewChargeHistoryResponse['transactions'];
}> = ({ transactions }) => {
  const { setCurrentLineItemsView } = useViewChargedDetailsContext();
  if (!transactions || transactions.length === 0) {
    return <>No transactions available.</>;
  }

  return (
    <ProgressStepper
      orientation="vertical"
      style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {transactions.map((transaction, index) => (
        <Step key={index} style={{ width: '100%' }}>
          <div className="sui-flex sui-items-center sui-justify-between sui-w-full">
            <div className="sui-flex sui-items-center">
              <StepButton
                icon={getTransactionIcon(transaction.transactionType)}
              />
              <StepLabel
                title={transaction.transactionDescription}
                subtitle={getTenderSubtitle(transaction)}
              />
            </div>
            <div className="sui-flex sui-items-center sui-gap-2">
              <Typography variant="body-base" weight="bold">
                ${transaction.transactionAmount}
              </Typography>
              <IconButton
                icon={ArrowForward}
                onClick={() => {
                  setCurrentLineItemsView({
                    transactionDetails: transaction.transactionDescription,
                    transaction,
                  });
                }}
              />
            </div>
          </div>
        </Step>
      ))}
    </ProgressStepper>
  );
};
